import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import * as XLSX from "xlsx";
import "../styles/grid.css";
import FormulaBar from "./FormulaBar";
import { computeSummary, evaluateField, evaluateSummaryFormula } from "../services/data";

// ─── Column Definitions ────────────────────────────────────────────────────

const SALES_COL_DEFS = [
  { field: "id", headerName: "#", width: 58, editable: false },
  { field: "product", headerName: "Product", width: 130, editable: true },
  { field: "region", headerName: "Region", width: 100, editable: true },
  { field: "manager", headerName: "Sales Manager", width: 150, editable: true },
  { field: "quarter", headerName: "Quarter", width: 95, editable: true },
  {
    field: "unitsSold", headerName: "Units Sold", width: 105, editable: true,
    type: "numericColumn",
    valueFormatter: p => (p.value != null ? Number(p.value).toLocaleString() : ""),
  },
  {
    field: "unitPrice", headerName: "Unit Price ($)", width: 120, editable: true,
    type: "numericColumn",
    valueFormatter: p => (p.value != null ? `$${Number(p.value).toFixed(2)}` : ""),
  },
  {
    field: "revenue", headerName: "Revenue ($)", width: 130, editable: true,
    type: "numericColumn",
    valueGetter: p => evaluateField(p.data, "revenue"),
    valueSetter: p => { p.data.revenue = p.newValue; return true; },
    valueFormatter: p => (p.value != null ? `$${Number(p.value).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : ""),
    cellStyle: () => ({ color: "#10b981", fontWeight: "600" }),
  },
  {
    field: "cogs", headerName: "COGS ($)", width: 115, editable: true,
    type: "numericColumn",
    valueGetter: p => evaluateField(p.data, "cogs"),
    valueSetter: p => { p.data.cogs = p.newValue; return true; },
    valueFormatter: p => (p.value != null ? `$${Number(p.value).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : ""),
  },
  {
    field: "grossProfit", headerName: "Gross Profit ($)", width: 130, editable: true,
    type: "numericColumn",
    valueGetter: p => evaluateField(p.data, "grossProfit"),
    valueSetter: p => { p.data.grossProfit = p.newValue; return true; },
    valueFormatter: p => (p.value != null ? `$${Number(p.value).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : ""),
    cellStyle: p => ({ color: Number(p.value) >= 0 ? "#10b981" : "#ef4444", fontWeight: "600" }),
  },
  {
    field: "expenses", headerName: "Expenses ($)", width: 115, editable: true,
    type: "numericColumn",
    valueGetter: p => evaluateField(p.data, "expenses"),
    valueSetter: p => { p.data.expenses = p.newValue; return true; },
    valueFormatter: p => (p.value != null ? `$${Number(p.value).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : ""),
  },
  {
    field: "netProfit", headerName: "Net Profit ($)", width: 130, editable: true,
    type: "numericColumn",
    valueGetter: p => evaluateField(p.data, "netProfit"),
    valueSetter: p => { p.data.netProfit = p.newValue; return true; },
    valueFormatter: p => (p.value != null ? `$${Number(p.value).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : ""),
    cellStyle: p => ({ color: Number(p.value) >= 0 ? "#10b981" : "#ef4444", fontWeight: "700" }),
  },
  {
    field: "margin", headerName: "Margin %", width: 100, editable: true,
    type: "numericColumn",
    valueGetter: p => evaluateField(p.data, "margin"),
    valueSetter: p => { p.data.margin = p.newValue; return true; },
    valueFormatter: p => (p.value != null ? `${Number(p.value).toFixed(2)}%` : ""),
    cellStyle: p => ({ color: Number(p.value) >= 15 ? "#10b981" : Number(p.value) >= 5 ? "#f59e0b" : "#ef4444" }),
  },
  {
    field: "status", headerName: "Status", width: 115, editable: true,
    cellStyle: p => {
      const m = { Completed: "#10b981", Pending: "#f59e0b", "In Progress": "#60a5fa", Cancelled: "#ef4444" };
      return { color: m[p.value] || "inherit", fontWeight: "600" };
    },
  },
];

const SUMMARY_COL_DEFS = [
  {
    field: "metric", headerName: "Metric", width: 200, editable: true, pinned: "left",
    cellStyle: { fontWeight: "600" },
  },
  {
    field: "value", headerName: "Result", width: 480, editable: true,
    valueFormatter: p => p.data.value,
    valueGetter: p => p.data.formula,
    valueSetter: p => {
      p.data.formula = p.newValue;
      p.data.value = evaluateSummaryFormula(p.newValue, p.context.salesData, p.data.metric, p.context.namedRanges);
      return true;
    },
    cellStyle: p => ({ fontWeight: "700", color: String(p.data?.value || "").startsWith("-") ? "#ef4444" : "#10b981" }),
  },
  {
    field: "note", headerName: "Description", width: 300, editable: true,
    cellStyle: { color: "#94a3b8", fontStyle: "italic", fontSize: "12px" },
  },
];

const FREEZABLE = SALES_COL_DEFS.filter(c => c.field !== "id").map(c => ({ field: c.field, headerName: c.headerName }));

// ─── DataGrid Component ────────────────────────────────────────────────────

function DataGrid({ rowData, dark, tabId = "default", updateTabData, isSummarySheet, salesData, summaryData, namedRanges = [], updateNamedRanges }) {
  const gridRef = useRef();

  // Internal data – initialised from rowData / salesData, never loops back
  const [data, setData] = useState([]);
  const [autosave, setAutosave] = useState(true);

  const [showNameManager, setShowNameManager] = useState(false);

  // Formula bar
  const [cellAddress, setCellAddress] = useState("");
  const [formulaDisplay, setFormulaDisplay] = useState("");
  const [selField, setSelField] = useState(null);
  const [selRowIdx, setSelRowIdx] = useState(null);

  // Column freeze
  const [frozenFields, setFrozenFields] = useState(new Set(["product"]));
  const [showFreezeMenu, setShowFreezeMenu] = useState(false);

  // UI
  const [quickFilter, setQuickFilter] = useState("");
  const [showFind, setShowFind] = useState(false);
  const [findText, setFindText] = useState("");
  const [replaceText, setReplaceText] = useState("");

  // ── Load data ONCE on mount / tab switch (no dependency on `data`)  ────────
  useEffect(() => {
    if (isSummarySheet) {
      setData(computeSummary(salesData || [], summaryData, namedRanges));
    } else {
      setData(rowData && rowData.length > 0 ? rowData : []);
    }
    // Reset formula bar when sheet changes
    setCellAddress("");
    setFormulaDisplay("");
    setSelField(null);
    setSelRowIdx(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId, isSummarySheet]); // intentionally NOT including rowData/salesData to avoid loop

  // ── When salesData changes externally (e.g., load template) ───────────────
  useEffect(() => {
    if (isSummarySheet) {
      setData(computeSummary(salesData || [], summaryData, namedRanges));
    }
  }, [salesData, summaryData, namedRanges, isSummarySheet]);

  // ── Autosave ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isSummarySheet && autosave && data.length > 0 && tabId !== "default") {
      try {
        localStorage.setItem(`agData_${tabId}`, JSON.stringify(data));
      } catch (e) {
        console.warn("Autosave skipped: data exceeds browser local storage limits.");
      }
    }
  }, [data, autosave, tabId, isSummarySheet]);

  // ── Column defs (apply freeze pinning) ────────────────────────────────────
  const columnDefs = useMemo(() => {
    if (isSummarySheet) return SUMMARY_COL_DEFS;
    return SALES_COL_DEFS.map(col => ({
      ...col,
      pinned: frozenFields.has(col.field) ? "left" : null,
      filter: true,
      sortable: true,
      resizable: true,
      enableCellChangeFlash: true,
    }));
  }, [frozenFields, isSummarySheet]);

  const defaultColDef = useMemo(() => ({
    editable: !isSummarySheet,   // ← all sales cells editable by default
    sortable: true,
    filter: true,
    resizable: true,
    enableCellChangeFlash: true,
    filterParams: { buttons: ["reset", "apply"] },
    suppressKeyboardEvent: params => params.editing && params.event.key === "Enter", // allow natural Enter in editor
  }), [isSummarySheet]);

  // ── Cell focused → update formula bar ────────────────────────────────────
  const onCellFocused = useCallback((e) => {
    if (!e.rowIndex == null || !e.column) return;
    const api = e.api;
    const rowIdx = e.rowIndex;
    const col = e.column;
    if (rowIdx == null || !col) { setCellAddress(""); setFormulaDisplay(""); return; }

    const field = col.getColId();
    const node = api.getDisplayedRowAtIndex(rowIdx);
    if (!node) return;
    const rowData = node.data;

    // Build Excel-style column letter
    const allCols = api.getAllDisplayedColumns();
    const colIdx = allCols.indexOf(col);
    let letter = "";
    let n = colIdx;
    do {
      letter = String.fromCharCode(65 + (n % 26)) + letter;
      n = Math.floor(n / 26) - 1;
    } while (n >= 0);

    setCellAddress(`${letter}${rowIdx + 1}`);
    setSelField(field);
    setSelRowIdx(rowIdx);

    if (isSummarySheet) {
      // Show the row's formula string in formula bar, but in 'value' field editing!
      setFormulaDisplay(rowData?.formula || String(rowData?.[field] ?? ""));
    } else {
      // Show raw value
      setFormulaDisplay(rowData?.[field] != null ? String(rowData[field]) : "");
    }
  }, [isSummarySheet]);

  // Helper: get ALL current rows from AG-Grid (avoids stale React state closure)
  const getAllGridRows = useCallback(() => {
    const rows = [];
    gridRef.current?.api?.forEachNode(node => { if (node.data) rows.push(node.data); });
    return rows;
  }, []);

  // ── Formula bar apply ─────────────────────────────────────────────────────
  const handleFormulaApply = useCallback((newVal) => {
    if (selRowIdx == null || !selField) return;

    const parsed = (newVal !== "" && !isNaN(Number(newVal))) ? Number(newVal) : newVal;

    // Update via AG-Grid node so the cell re-renders without full data reload
    if (!gridRef.current?.api) return;
    const node = gridRef.current.api.getDisplayedRowAtIndex(selRowIdx);
    if (!node) return;

    // Push value to AG-Grid node data so valueGetter/setters work naturally
    node.setDataValue(selField, parsed);

    // Crucially refresh cells, AG Grid will re-trigger valueGetter for formula-dependent cells!
    gridRef.current.api.refreshCells({ force: true });

    // Read ALL rows from grid (fresh, avoids stale closure)
    const allRows = getAllGridRows();
    setData([...allRows]);
    updateTabData?.([...allRows]);

    setFormulaDisplay(String(parsed));
  }, [selRowIdx, selField, updateTabData, getAllGridRows]);

  // ── Cell value changed (in-grid direct edit) ──────────────────────────────
  const onCellValueChanged = useCallback((params) => {
    // Refresh all calculated columns so dependencies update
    params.api.refreshCells({ force: true });

    const allRows = [];
    params.api.forEachNode(node => { if (node.data) allRows.push(node.data); });

    // 1. Sync React state
    setData([...allRows]);

    // 2. Push to Dashboard
    updateTabData?.([...allRows]);

    // 3. Sync formula bar for the currently focused cell
    if (params.rowIndex === selRowIdx && params.column.getColId() === selField) {
      setFormulaDisplay(params.newValue != null ? String(params.newValue) : "");
    }
  }, [updateTabData, selRowIdx, selField]);

  // ── Toolbar actions ───────────────────────────────────────────────────────
  const addRow = () => {
    // Use functional form so we always have the latest data
    setData(prev => {
      const newRow = isSummarySheet
        ? { metric: "New Metric", formula: "=0", value: "0", note: "" }
        : {
          id: prev.length + 1, product: "New Product", region: "North",
          manager: "Manager", quarter: "Q1 2024", unitsSold: 0, unitPrice: 0,
          revenue: 0, cogs: 0, grossProfit: 0, expenses: 0, netProfit: 0, margin: 0, status: "Pending",
        };
      const updated = [...prev, newRow];
      updateTabData?.(updated);
      return updated;
    });
  };

  const deleteRow = () => {
    if (!gridRef.current?.api) return;
    const selected = gridRef.current.api.getSelectedRows();
    if (!selected.length) return;
    const allRows = getAllGridRows().filter(r => !selected.includes(r));
    setData([...allRows]);
    updateTabData?.([...allRows]);
  };

  const duplicateRow = () => {
    if (!gridRef.current?.api) return;
    const selected = gridRef.current.api.getSelectedRows();
    if (!selected.length) return;
    setData(prev => {
      const updated = [...prev, isSummarySheet ? { ...selected[0] } : { ...selected[0], id: prev.length + 1 }];
      updateTabData?.(updated);
      return updated;
    });
  };

  const autoSizeAll = () => {
    if (!gridRef.current?.api) return;
    const colIds = gridRef.current.api.getAllDisplayedColumns().map(c => c.getColId());
    gridRef.current.api.autoSizeColumns(colIds, false);
  };

  const exportCSV = () => gridRef.current?.api?.exportDataAsCsv({ fileName: 'AG_Finance_Export.csv' });

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();

    // 1. Generate SalesData sheet
    const fields = SALES_COL_DEFS.map(c => c.field);
    const salesHeader = SALES_COL_DEFS.map(c => c.headerName);

    // Fallback to rowData if salesData isn't available
    const activeSales = salesData || rowData || [];
    const salesDataRaw = activeSales.map(row => fields.map(f => row[f]));
    const wsSales = XLSX.utils.aoa_to_sheet([salesHeader, ...salesDataRaw]);

    activeSales.forEach((row, rIdx) => {
      fields.forEach((f, cIdx) => {
        const raw = row[f];
        if (typeof raw === 'string' && raw.startsWith('=')) {
          const cellRef = XLSX.utils.encode_cell({ r: rIdx + 1, c: cIdx });
          const cell = wsSales[cellRef] || { t: 'n' };
          cell.t = 'n';
          cell.f = raw.substring(1);
          cell.v = Number(evaluateField(row, f)) || 0;
          wsSales[cellRef] = cell;
        } else {
          // AGGrid types
          const colDef = SALES_COL_DEFS[cIdx];
          const cellRef = XLSX.utils.encode_cell({ r: rIdx + 1, c: cIdx });
          const cell = wsSales[cellRef] || { v: raw };
          if (colDef && colDef.type === "numericColumn" && !isNaN(Number(raw))) {
            cell.t = 'n';
            cell.v = Number(raw);
          }
          wsSales[cellRef] = cell;
        }
      });
    });
    XLSX.utils.book_append_sheet(wb, wsSales, "SalesData");

    // 2. Generate Summary sheet
    const activeSummary = summaryData || computeSummary(activeSales);
    const summaryHeader = ["Metric", "Result", "Description"];
    const wsSummary = XLSX.utils.aoa_to_sheet([summaryHeader]);

    activeSummary.forEach((row, rIdx) => {
      const rowNum = rIdx + 1;

      wsSummary[XLSX.utils.encode_cell({ r: rowNum, c: 0 })] = { t: 's', v: String(row.metric || '') };

      const resCell = { t: 'n' };
      if (typeof row.formula === 'string' && row.formula.startsWith('=')) {
        let f = row.formula.substring(1);

        const rangeMap = {
          REVENUE: 'SalesData!H2:H100000', COGS: 'SalesData!I2:I100000', GROSSPROFIT: 'SalesData!J2:J100000',
          EXPENSES: 'SalesData!K2:K100000', NETPROFIT: 'SalesData!L2:L100000', MARGIN: 'SalesData!M2:M100000',
          UNITSSOLD: 'SalesData!F2:F100000', UNITPRICE: 'SalesData!G2:G100000',
          REGION: 'SalesData!C2:C100000', PRODUCT: 'SalesData!B2:B100000'
        };
        (namedRanges || []).forEach(({ name, range }) => {
          if (name && range) rangeMap[name.toUpperCase()] = range;
        });
        const keys = Object.keys(rangeMap).sort((a, b) => b.length - a.length);
        const regex = new RegExp(`\\b(${keys.join('|')})\\b`, 'gi');
        f = f.replace(regex, (m) => {
          return rangeMap[m.toUpperCase()] || m;
        });

        resCell.f = f;
        const valStr = String(row.value).replace(/[^0-9.-]/g, '');
        resCell.v = Number(valStr) || 0;
        if (row.formula.includes('INDEX')) {
          resCell.t = 's';
          resCell.v = row.value;
        }
      } else {
        resCell.t = 's';
        resCell.v = String(row.formula || row.value || '');
      }
      wsSummary[XLSX.utils.encode_cell({ r: rowNum, c: 1 })] = resCell;
      wsSummary[XLSX.utils.encode_cell({ r: rowNum, c: 2 })] = { t: 's', v: String(row.note || '') };
    });

    wsSummary['!ref'] = XLSX.utils.encode_range({ s: { c: 0, r: 0 }, e: { c: 2, r: activeSummary.length } });
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

    XLSX.writeFile(wb, "AG_Finance_Export.xlsx");
  };

  const toggleFreeze = (field) =>
    setFrozenFields(prev => { const n = new Set(prev); n.has(field) ? n.delete(field) : n.add(field); return n; });

  const handleFindReplace = () => {
    if (!findText) return;
    setData(prev => {
      const updated = prev.map(row => {
        const r = { ...row };
        Object.keys(r).forEach(k => {
          const s = String(r[k]);
          if (s.includes(findText)) {
            const rep = s.replaceAll(findText, replaceText);
            r[k] = (!isNaN(Number(rep)) && rep !== "") ? Number(rep) : rep;
          }
        });
        return r;
      });
      updateTabData?.(updated);
      return updated;
    });
    setShowFind(false); setFindText(""); setReplaceText("");
  };

  // ── Design tokens ─────────────────────────────────────────────────────────
  const T = dark ? {
    bg: "#0f1624", surface: "#141d2e", border: "#1e2843",
    text: "#c8d6ef", muted: "#4a5a7a",
    btnBg: "#1a2540", btnHover: "#1f2d4a", btnBorder: "#1e2843",
    btnText: "#8899bb", accent: "#3b82f6",
  } : {
    bg: "#f8fafc", surface: "#ffffff", border: "#e2e8f0",
    text: "#1e293b", muted: "#94a3b8",
    btnBg: "#f1f5f9", btnHover: "#e2e8f0", btnBorder: "#e2e8f0",
    btnText: "#475569", accent: "#2563eb",
  };

  const statusBar = useMemo(() => ({
    statusPanels: [
      { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" },
      { statusPanel: "agSelectedRowCountComponent", align: "left" },
      { statusPanel: "agAggregationComponent", align: "right" },
    ],
  }), []);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", overflow: "hidden", background: T.bg }}>

      {/* ── Formula Bar ─────────────────────────────────────── */}
      <FormulaBar
        cellAddress={cellAddress}
        formulaDisplay={formulaDisplay}
        onFormulaApply={handleFormulaApply}
        readOnly={false}
        dark={dark}
      />

      {/* ── Toolbar ──────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", flexWrap: "wrap", gap: "5px",
        padding: "5px 10px", background: T.surface,
        borderBottom: `1px solid ${T.border}`,
        flexShrink: 0, minHeight: "42px",
      }}>

        {/* Row ops */}
        <div style={{ display: "flex", border: `1px solid ${T.btnBorder}`, borderRadius: "7px", overflow: "hidden", boxShadow: "0 1px 2px rgba(0,0,0,0.06)" }}>
          {[
            { label: "Add Row", icon: "+", color: "#10b981", fn: addRow },
            { label: "Duplicate", icon: "⊕", color: "#3b82f6", fn: duplicateRow },
            { label: "Delete Row", icon: "−", color: "#ef4444", fn: deleteRow },
          ].map((b, i) => (
            <React.Fragment key={b.label}>
              {i > 0 && <div style={{ width: "1px", background: T.btnBorder }} />}
              <TinyBtn T={T} onClick={b.fn}>
                <span style={{ color: b.color, fontWeight: "700" }}>{b.icon}</span>
                {b.label}
              </TinyBtn>
            </React.Fragment>
          ))}
        </div>

        <PillBtn T={T} onClick={autoSizeAll} title="Auto-size all columns">⇔ Auto Fit</PillBtn>

        {/* Freeze dropdown */}
        {!isSummarySheet && (
          <div style={{ position: "relative" }}>
            <PillBtn T={T} active={frozenFields.size > 0} onClick={() => setShowFreezeMenu(v => !v)}>
              🔒 Freeze {frozenFields.size > 0 ? `(${frozenFields.size})` : ""}
            </PillBtn>
            {showFreezeMenu && (
              <div style={{
                position: "absolute", top: "calc(100% + 5px)", left: 0, zIndex: 1000,
                background: T.surface, border: `1px solid ${T.border}`, borderRadius: "10px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.22)", minWidth: "215px", padding: "6px 0",
              }}>
                <div style={{ padding: "5px 14px 3px", fontSize: "10px", fontWeight: "700", color: T.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  Pin columns to the left
                </div>
                {FREEZABLE.map(col => (
                  <label key={col.field} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 14px", cursor: "pointer", fontSize: "12.5px", color: T.text }}
                    onMouseEnter={e => e.currentTarget.style.background = T.btnHover}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <input type="checkbox" checked={frozenFields.has(col.field)} onChange={() => toggleFreeze(col.field)}
                      style={{ accentColor: T.accent, width: "14px", height: "14px", cursor: "pointer" }} />
                    <span style={{ flex: 1 }}>{col.headerName}</span>
                    {frozenFields.has(col.field) && <span style={{ fontSize: "9px", fontWeight: "700", color: T.accent }}>FROZEN</span>}
                  </label>
                ))}
                <div style={{ borderTop: `1px solid ${T.border}`, margin: "4px 14px" }} />
                <button onClick={() => { setFrozenFields(new Set()); setShowFreezeMenu(false); }}
                  style={{ width: "100%", textAlign: "left", padding: "5px 14px", background: "none", border: "none", cursor: "pointer", fontSize: "12px", color: "#ef4444", fontWeight: "500" }}>
                  Clear all freezes
                </button>
              </div>
            )}
          </div>
        )}

        {/* Quick filter */}
        <div style={{ display: "flex", alignItems: "center", gap: "5px", border: `1px solid ${T.btnBorder}`, borderRadius: "7px", padding: "0 9px", background: T.btnBg, height: "29px" }}>
          <span style={{ color: T.muted, fontSize: "12px" }}>🔍</span>
          <input type="text" placeholder="Quick filter…" value={quickFilter} onChange={e => setQuickFilter(e.target.value)}
            style={{ border: "none", outline: "none", background: "transparent", fontSize: "12.5px", color: T.text, width: "130px" }} />
          {quickFilter && <span onClick={() => setQuickFilter("")} style={{ cursor: "pointer", color: T.muted, fontSize: "13px" }}>✕</span>}
        </div>

        {!isSummarySheet && (
          <PillBtn T={T} active={showFind} onClick={() => setShowFind(v => !v)}>⇄ Find/Replace</PillBtn>
        )}
        <PillBtn T={T} onClick={() => setShowNameManager(true)}>🏷️ Name Manager</PillBtn>

        <div style={{ flex: 1 }} />

        {/* Autosave */}
        {!isSummarySheet && (
          <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "12px", color: T.btnText, userSelect: "none" }}>
            Auto-Save
            <div onClick={() => setAutosave(v => !v)} style={{
              width: "30px", height: "16px", borderRadius: "9px",
              background: autosave ? "#10b981" : T.border,
              position: "relative", cursor: "pointer", transition: "background 0.2s", flexShrink: 0,
            }}>
              <div style={{
                position: "absolute", top: "2px", left: autosave ? "14px" : "2px",
                width: "12px", height: "12px", borderRadius: "50%",
                background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              }} />
            </div>
          </label>
        )}

        <PillBtn T={T} onClick={exportExcel} title="Export entire workbook to Excel">📊 Export Excel</PillBtn>
        <PillBtn T={T} onClick={exportCSV} title="Export current view to CSV">📄 Export CSV</PillBtn>
      </div>

      {/* ── Find/Replace panel ──────────────────────────────── */}
      {showFind && !isSummarySheet && (
        <div style={{
          display: "flex", alignItems: "center", gap: "8px", padding: "5px 12px", flexShrink: 0,
          background: dark ? "#111828" : "#fefce8", borderBottom: `1px solid ${T.border}`,
          fontSize: "12.5px", color: T.text,
        }}>
          <span style={{ fontWeight: "600", color: T.muted }}>Find & Replace</span>
          <input type="text" value={findText} onChange={e => setFindText(e.target.value)} placeholder="Find…"
            style={{ padding: "3px 8px", border: `1px solid ${T.border}`, borderRadius: "5px", background: T.surface, color: T.text, outline: "none", fontSize: "12px", width: "130px" }} />
          <span style={{ color: T.muted }}>→</span>
          <input type="text" value={replaceText} onChange={e => setReplaceText(e.target.value)} placeholder="Replace with…"
            style={{ padding: "3px 8px", border: `1px solid ${T.border}`, borderRadius: "5px", background: T.surface, color: T.text, outline: "none", fontSize: "12px", width: "130px" }} />
          <button onClick={handleFindReplace}
            style={{ padding: "3px 12px", background: T.accent, color: "#fff", border: "none", borderRadius: "5px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>
            Replace All
          </button>
          <button onClick={() => setShowFind(false)}
            style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: T.muted, fontSize: "18px" }}>×</button>
        </div>
      )}

      {/* ── AG Grid ──────────────────────────────────────────── */}
      <div className={dark ? "ag-theme-alpine-dark" : "ag-theme-alpine"} style={{ flex: 1, width: "100%", minHeight: 0 }}>
        <AgGridReact
          ref={gridRef}
          context={{ salesData, namedRanges }}
          rowData={data}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection="multiple"
          animateRows={true}
          rowHeight={34}
          headerHeight={38}
          quickFilterText={quickFilter}
          statusBar={statusBar}
          suppressMovableColumns={false}
          enableCellChangeFlash={true}
          singleClickEdit={true}
          stopEditingWhenCellsLoseFocus={true}
          onCellFocused={onCellFocused}
          onCellValueChanged={onCellValueChanged}
          rowClassRules={{ "row-stripe": p => p.rowIndex % 2 === 1 }}
          suppressContextMenu={false}
        />
      </div>

      {/* Close freeze menu overlay */}
      {showFreezeMenu && (
        <div onClick={() => setShowFreezeMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 999 }} />
      )}

      {/* Name Manager Modal */}
      {showNameManager && (
        <NameManagerModal
          namedRanges={namedRanges}
          updateNamedRanges={updateNamedRanges}
          onClose={() => setShowNameManager(false)}
          T={T}
        />
      )}
    </div>
  );
}

// ─── Mini UI components ────────────────────────────────────────────────────

function TinyBtn({ children, onClick, T }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        display: "flex", alignItems: "center", gap: "4px",
        padding: "0 10px", height: "29px",
        background: h ? T.btnHover : T.btnBg,
        border: "none", cursor: "pointer",
        fontSize: "12px", color: T.btnText, fontWeight: "500",
        transition: "background 0.12s", whiteSpace: "nowrap",
      }}>
      {children}
    </button>
  );
}

function PillBtn({ children, onClick, T, title, active }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={onClick} title={title}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        display: "flex", alignItems: "center", gap: "4px",
        padding: "0 10px", height: "29px",
        background: active ? "rgba(59,130,246,0.12)" : (h ? T.btnHover : T.btnBg),
        border: `1px solid ${active ? "rgba(59,130,246,0.35)" : T.btnBorder}`,
        borderRadius: "7px", cursor: "pointer",
        fontSize: "12px",
        color: active ? T.accent : T.btnText,
        fontWeight: active ? "600" : "500",
        transition: "all 0.12s", whiteSpace: "nowrap",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      }}>
      {children}
    </button>
  );
}

export default DataGrid;

function NameManagerModal({ namedRanges, updateNamedRanges, onClose, T }) {
  const [ranges, setRanges] = useState(namedRanges || []);

  const addRange = () => {
    setRanges([...ranges, { name: "", range: "" }]);
  };

  const updateRange = (index, field, value) => {
    const newRanges = [...ranges];
    newRanges[index][field] = value;
    setRanges(newRanges);
  };

  const deleteRange = (index) => {
    setRanges(ranges.filter((_, i) => i !== index));
  };

  const save = () => {
    const cleanRanges = ranges.filter(r => r.name.trim() !== "");
    updateNamedRanges?.(cleanRanges);
    onClose();
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100
    }}>
      <div style={{
        background: T.surface, width: "450px", borderRadius: "10px", padding: "20px",
        boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)", border: `1px solid ${T.border}`,
      }}>
        <h3 style={{ margin: "0 0 15px 0", color: T.text, fontSize: "16px" }}>🏷️ Named Ranges Manager</h3>
        <p style={{ fontSize: "12px", color: T.muted, marginBottom: "15px" }}>
          Define custom range names for use in formulas. (e.g. Name: <b>TAX</b>, Range: <b>0.05</b> or <b>SalesData!J2:J100</b>).
        </p>

        <div style={{ maxHeight: "300px", overflowY: "auto", marginBottom: "15px" }}>
          {ranges.map((r, i) => (
            <div key={i} style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
              <input
                type="text" placeholder="Name (e.g. TARGET)" value={r.name}
                onChange={e => updateRange(i, "name", e.target.value)}
                style={{ flex: 1, padding: "6px 8px", borderRadius: "4px", border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: "12.5px" }}
              />
              <input
                type="text" placeholder="Range (e.g. SalesData!A1:A5)" value={r.range}
                onChange={e => updateRange(i, "range", e.target.value)}
                style={{ flex: 1, padding: "6px 8px", borderRadius: "4px", border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: "12.5px" }}
              />
              <button
                onClick={() => deleteRange(i)}
                style={{ padding: "6px 10px", background: "rgba(239,68,68,0.15)", color: "#ef4444", border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "bold" }}
              >×</button>
            </div>
          ))}
          {ranges.length === 0 && <div style={{ fontSize: "12px", color: T.muted }}>No named ranges defined.</div>}
        </div>

        <button onClick={addRange} style={{
          padding: "6px 12px", background: T.btnBg, color: T.accent, border: `1px solid ${T.btnBorder}`, borderRadius: "4px", cursor: "pointer", fontSize: "12px", fontWeight: "600", marginBottom: "20px"
        }}>+ Add Named Range</button>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "10px", paddingTop: "15px", borderTop: `1px solid ${T.border}` }}>
          <button onClick={onClose} style={{
            padding: "8px 16px", background: "transparent", color: T.text, border: "none", cursor: "pointer", fontSize: "13px", fontWeight: "500"
          }}>Cancel</button>
          <button onClick={save} style={{
            padding: "8px 16px", background: T.accent, color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: "600"
          }}>Save Named Ranges</button>
        </div>
      </div>
    </div>
  );
}
